import {Injectable, NgZone} from '@angular/core';
import {JsonCodecService} from './json-codec.service';
import {MessageType} from './message-type';
import {SessionBehaviour} from './session-behaviour';
import {SessionContext} from './session-context';

interface WebSocketMessage extends Record<string, any> {
    login?: { credentials: string; credentials_secret: string };
    login_accepted?: any;
    login_rejected?: any;
    session_started?: { default_channel_id: string };
    heartbeat?: { timestamp: number };
    channel_added?: { channel_id: string; next_sequence_number: string };
    channel_removed?: { channel_id: string };
    request_stream?: {
        channel_id: number;
        begin_sequence_number: number;
        end_sequence_number: string;
        stream_id: number;
    };
    stream_started?: { channel_id: string; begin_sequence_number: string };
    stream_ended?: any;
    session_ended?: any;
    message_rejected?: { text: string };
}

@Injectable()
export class SessionWebsocketService {
    private ws!: WebSocket;
    private heartbeatIntervalId: any;
    private messageQueue: Map<number, (data: any) => void> = new Map();
    private messageIdCounter = 0;

    constructor(
        private codec: JsonCodecService,
        private zone: NgZone
    ) {}

    public async connect(config: {
        url: string;
        credentials: string;
        secret: string;
        context?: SessionContext;
        behaviour?: SessionBehaviour;
        heartbeatInterval?: number;
    }): Promise<void> {
        const {
                  url,
                  credentials,
                  secret,
                  context = SessionContext.allMessages(),
                  behaviour = SessionBehaviour.simple(),
                  heartbeatInterval = 15
              } = config;

        this.ws = new WebSocket(url);

        return new Promise((resolve, reject) => {
            this.ws.onopen = async () => {
                try {
                    await this.login(credentials, secret);
                    await this.expectLoginReply();
                    await this.expectSessionStarted();

                    // Initialize heartbeat
                    this.heartbeatIntervalId = setInterval(() => {
                        this.sendHeartbeat();
                    }, heartbeatInterval * 1000);

                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            this.ws.onmessage = (event) => this.handleMessage(event);

            this.ws.onerror = (error) => {
                reject(error);
            };

            this.ws.onclose = () => {
                clearInterval(this.heartbeatIntervalId);
                this.handleConnectionClosed();
            };
        });
    }

    public async send(data: any, type: MessageType = MessageType.SYNC): Promise<void> {
        this.sendMessage(data);
    }

    public async recv(timeout: number = 5000): Promise<any> {
        console.log('recv'); // TODO

        return this.receiveMessage();
    }

    public async close(): Promise<void> {
        clearInterval(this.heartbeatIntervalId);
        this.ws.close();
    }

    private async login(credentials: string, secret: string): Promise<void> {
        const message = {
            login: {
                credentials,
                credentials_secret: secret
            }
        };
        this.sendMessage(message);
    }

    private async expectLoginReply(): Promise<void> {
        console.log('expectLoginReply'); // TODO

        const reply = await this.receiveMessage();
        if (reply.login_accepted) return;
        if (reply.login_rejected) {
            throw new Error('Login rejected');
        }
        throw new Error('Unexpected reply');
    }

    private async expectSessionStarted(): Promise<void> {
        console.log('expectSessionStarted'); // TODO

        const reply = await this.receiveMessage();
        if (reply.session_started) return;
        throw new Error('Session not started');
    }

    private sendHeartbeat(): void {
        const message = {
            heartbeat: {
                timestamp: Date.now()
            }
        };
        this.sendMessage(message);
    }

    private sendMessage(message: any): void {
        const encoded = this.codec.encode(message);
        this.ws.send(encoded);
    }

    private receiveMessage(timeout: number = 5000): Promise<WebSocketMessage> {
        return new Promise((resolve, reject) => {
            const id = ++this.messageIdCounter;
            this.messageQueue.set(id, (data) => {
                resolve(data);
            });

            // Timeout after 5 seconds
            setTimeout(() => {
                this.messageQueue.delete(id);
                reject(new Error('Message timeout'));
            }, timeout);
        });
    }

    private handleConnectionClosed(): void {
        // Logic for reconnecting or notifying subscribers
        console.log('WebSocket connection closed');
    }

    private handleMessage(event: MessageEvent): void {
        this.zone.run(() => {
            try {
                const data = this.codec.decode(event.data as string);

                if (data.heartbeat) {
                    return;
                }

                // TODO
                // if (data.login_accepted) {
                //     this.resolveNextPending(data);
                //     return;
                // }
                //
                // if (data.session_started) {
                //     this.defaultChannelId = parseInt(data.session_started.default_channel_id);
                //     this.resolveNextPending(data);
                //     return;
                // }
                //
                // if (data.channel_added) {
                //     const channelId = parseInt(data.channel_added.channel_id);
                //     this.unusedChannels.add(channelId);
                //
                //     if (this.currentChannelId !== channelId) {
                //         this.currentChannelId = channelId;
                //     }
                //
                //     return;
                // }
                //
                // if (data.stream_started) {
                //     const channelId = parseInt(data.stream_started.channel_id);
                //     if (this.defaultChannelId === channelId) {
                //         this.resolveNextPending(data);
                //     }
                //     return;
                // }
                //
                // this.resolveNextPending(data);
            } catch (error) {
                console.error('Error handling message:', error);
            }
        });
    }
}
